<template>
  <PageLayout :title="$t('admin.canva_reports_title')" :subtitle="$t('admin.canva_reports_subtitle')">
    <ContentCard>
      <div class="d-flex flex-wrap mb-3 gap-2">
        <select v-model="statusFilter" class="form-control form-control-sm" style="max-width:160px" @change="load">
          <option value="">{{ $t('admin.all_status') }}</option>
          <option value="open">{{ $t('admin.report_status_open') }}</option>
          <option value="resolved">{{ $t('admin.report_status_resolved') }}</option>
        </select>
        <button class="btn btn-sm btn-primary" @click="load">{{ $t('common.refresh') }}</button>
      </div>

      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else-if="!reports.length" class="text-center py-4 text-muted">
        <p class="mb-0">{{ $t('admin.no_canva_reports') }}</p>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead>
            <tr>
              <th>{{ $t('global.id') }}</th>
              <th>{{ $t('global.user') }}</th>
              <th>{{ $t('admin.canva_link') }}</th>
              <th>{{ $t('admin.design_id') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th>{{ $t('admin.description') }}</th>
              <th>{{ $t('global.date') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in reports" :key="r.id">
              <td>{{ r.id }}</td>
              <td>
                <div>{{ r.user?.name || '-' }}</div>
                <small class="text-muted">{{ r.user?.email }}</small>
              </td>
              <td style="max-width:220px">
                <a :href="r.canva_url" target="_blank" rel="noopener noreferrer" class="d-block text-truncate">
                  {{ r.canva_url }}
                </a>
                <button type="button" class="btn btn-link btn-sm p-0" @click="openCanva(r.canva_url)">
                  {{ $t('admin.open_canva') }}
                </button>
              </td>
              <td>
                <router-link v-if="r.design_template_id" :to="`/ai/studio/${r.design_template_id}`" class="small">
                  {{ r.design_template_id }}
                </router-link>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <span class="badge" :class="r.status === 'resolved' ? 'badge-success' : 'badge-warning'">
                  {{ r.status === 'resolved' ? $t('admin.report_status_resolved') : $t('admin.report_status_open') }}
                </span>
              </td>
              <td class="report-msg" :title="r.message">{{ truncate(r.message, 120) }}</td>
              <td><small>{{ formatDate(r.created_at) }}</small></td>
              <td>
                <button
                  v-if="r.status === 'open'"
                  class="btn btn-xs btn-outline-success"
                  @click="onStatus(r, 'resolved')"
                >
                  {{ $t('admin.mark_resolved') }}
                </button>
                <button
                  v-else
                  class="btn btn-xs btn-outline-secondary"
                  @click="onStatus(r, 'open')"
                >
                  {{ $t('admin.reopen_report') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-muted small mb-0 mt-2">{{ $t('common.total_count', { count: reportsTotal }) }}</p>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    return {
      statusFilter: 'open',
    }
  },
  computed: {
    ...mapGetters('admin', ['canvaImportReports', 'canvaImportReportsTotal', 'loading']),
    reports() {
      return this.canvaImportReports || []
    },
    reportsTotal() {
      return this.canvaImportReportsTotal || 0
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    ...mapActions('admin', ['fetchCanvaImportReports', 'updateCanvaImportReport']),
    async load() {
      await this.fetchCanvaImportReports({
        status: this.statusFilter,
        page: 1,
        limit: 50,
      })
    },
    formatDate(v) {
      if (!v) return '-'
      return new Date(v).toLocaleString(this.intlLocale)
    },
    truncate(text, max) {
      const s = String(text || '')
      if (s.length <= max) return s
      return s.slice(0, max) + '…'
    },
    openCanva(url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async onStatus(report, status) {
      try {
        await this.updateCanvaImportReport({
          id: report.id,
          payload: { status },
          listParams: { status: this.statusFilter, page: 1, limit: 50 },
        })
        this.$toast.success(this.$t('admin.report_updated'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('common.load_failed'))
      }
    },
  },
}
</script>

<style scoped>
.report-msg {
  max-width: 280px;
  white-space: pre-wrap;
}
</style>
